import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Calendar, Check, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';

export default function Leaves() {
  const { user } = useAuth();
  const { isCrossCompany, selectedCompanyId } = useCompanyScope();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const [leaveTypesList, setLeaveTypesList] = useState([]);

  useEffect(() => {
    fetchEmployees();
    fetchLeaves();
    fetchLeaveTypes();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des employés');
    }
  };

  const fetchLeaves = async () => {
    try {
      const response = await api.get('/leaves');
      setLeaves(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des congés');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await api.get('/leave-types');
      setLeaveTypesList(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leaves', formData);
      toast.success('Demande de congé créée avec succès');
      setOpen(false);
      resetForm();
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await api.put(`/leaves/${leaveId}`, { status: 'approved' });
      toast.success('Congé approuvé');
      fetchLeaves();
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await api.put(`/leaves/${leaveId}`, { status: 'rejected' });
      toast.success('Congé rejeté');
      fetchLeaves();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      leave_type: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'N/A';
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { label: 'En attente', className: 'bg-warning text-white' },
      approved: { label: 'Approuve', className: 'bg-success text-white' },
      rejected: { label: 'Rejete', className: 'bg-error text-white' }
    };
    const variant = variants[status];
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const getLeaveTypeName = (leave) => {
    if (leave.leave_type_name) return leave.leave_type_name;
    const lt = leaveTypesList.find(t => t.id === leave.leave_type);
    if (lt) return lt.name;
    // Legacy fallback
    const legacy = { paid_leave: 'Conge annuel', sick_leave: 'Conge maladie', unpaid_leave: 'Conge sans solde', maternity_leave: 'Conge maternite', paternity_leave: 'Conge paternite' };
    return legacy[leave.leave_type] || leave.leave_type;
  };

  const filteredLeaves = useMemo(() => {
    if (!selectedCompanyId) return leaves;
    return leaves.filter((l) => l.company_id === selectedCompanyId);
  }, [leaves, selectedCompanyId]);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Gestion des Congés</h1>
          <p className="text-slate-600 mt-1">Demandes et validation des congés</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-700" data-testid="add-leave-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Demande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvelle Demande de Congé</DialogTitle>
              <DialogDescription>
                Remplissez les informations pour la demande
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employee">Employé *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} - Solde: {emp.leave_balance} jours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="leave_type">Type de conge *</Label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                  className="w-full rounded-md border border-slate-300 p-2"
                  required
                  data-testid="leave-type-select"
                >
                  <option value="">-- Choisir un type --</option>
                  {leaveTypesList.filter(lt => lt.is_active !== false).map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name} {lt.is_paid ? `(paye ${lt.pay_percentage}%)` : '(non paye)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date début *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Date fin *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="end-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motif *</Label>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  rows={3}
                  data-testid="reason-input"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="submit-leave-button">
                  Soumettre
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Demandes en attente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-warning">
              {leaves.filter(l => l.status === 'pending').length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Approuvés ce mois</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-success">
              {leaves.filter(l => l.status === 'approved').length}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Rejetés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-error">
              {leaves.filter(l => l.status === 'rejected').length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Liste des Demandes de Congés
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Employé</TableHead>
                  {isCrossCompany && <TableHead className="font-semibold">Entreprise</TableHead>}
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Dates</TableHead>
                  <TableHead className="font-semibold">Jours</TableHead>
                  <TableHead className="font-semibold">Motif</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isCrossCompany ? 8 : 7} className="text-center text-slate-500 py-8">
                      Aucune demande de congé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaves.map((leave) => (
                    <TableRow key={leave.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{getEmployeeName(leave.employee_id)}</TableCell>
                      {isCrossCompany && (
                        <TableCell>
                          <Badge variant="outline" className="bg-slate-50 text-slate-700">
                            {leave.company_name || '-'}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>{getLeaveTypeName(leave)}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {leave.start_date} → {leave.end_date}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {leave.days_count}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                      <TableCell>{getStatusBadge(leave.status)}</TableCell>
                      <TableCell className="text-right">
                        {leave.status === 'pending' && ['company_admin', 'hr', 'group_admin', 'group_hr'].includes(user.role) && (
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-success hover:bg-green-50"
                              onClick={() => handleApprove(leave.id)}
                              data-testid={`approve-leave-${leave.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-error hover:bg-red-50"
                              onClick={() => handleReject(leave.id)}
                              data-testid={`reject-leave-${leave.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
